/*
 * Ledge cart drawer — expand / collapse behaviour.
 *
 * The drawer's inner DOM is replaced by Horizon's cart-items-component on
 * every cart mutation (it uses morphSection, a DOM-diffing helper). Direct
 * click listeners on brand rows would survive a morph sometimes and die
 * other times, so we use EVENT DELEGATION on the document instead: one
 * listener, survives every re-render, zero bookkeeping.
 *
 * After each cart:update event we also re-sync the expand state to what
 * the server chose (most-recently-touched vendor expanded, others
 * collapsed). This is a belt-and-suspenders reset so a manual toggle the
 * user made before adding another item doesn't leak across into the fresh
 * drawer state. The server-rendered Liquid sets `data-default-expanded`
 * on each brand block; the JS reads that and rewrites `.is-expanded` +
 * aria-expanded to match.
 *
 * Keyboard accessibility comes for free because the brand-row toggle is a
 * native <button>. Enter / Space / focus ring / Tab order all work without
 * custom handlers. The close button, brand rows, per-item anchors, CTA and
 * Keep-shopping button are all focusable in DOM order.
 */
(function () {
  'use strict';

  var BRAND_SELECTOR = '.ledge-cart-drawer__brand';
  var HEADER_SELECTOR = '.ledge-cart-drawer__brand-header';

  /**
   * Click delegator on document. Survives every drawer re-render.
   */
  function onDocumentClick(event) {
    var header = event.target.closest ? event.target.closest(HEADER_SELECTOR) : null;
    if (!header) return;
    // Don't block Horizon's own close/CTA/link clicks — we only care about
    // the brand-toggle row.
    event.preventDefault();
    var block = header.closest(BRAND_SELECTOR);
    if (!block) return;
    var expanded = block.classList.toggle('is-expanded');
    header.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  /**
   * After cart mutates:
   *   1. Force expand state back to the server's intent (most-recent
   *      vendor expanded, others collapsed). Belt-and-braces in case
   *      Horizon's morphSection preserved a user toggle from before
   *      the add-to-cart.
   *   2. Ensure the drawer is open in MODAL state. Horizon's own
   *      showDialog path is race-prone here: the CartAddEvent handler
   *      schedules a rAF that calls dialog.showModal(), but morphSection
   *      may replace the <dialog> node between rAF scheduling and
   *      firing, leaving the captured dialog reference detached — the
   *      showModal call then no-ops silently. We reach into the CURRENT
   *      dialog ref and call showModal + body-scroll-lock ourselves if
   *      needed.
   */
  function onCartUpdate(event) {
    // cart:update fires AFTER Horizon's morphSection completes — the DOM
    // is in its final state at this point, so synchronous work here is
    // safe. No rAF needed, and avoiding it sidesteps environments that
    // pause rAF when the tab isn't focused (headless Chromiums, bfcache
    // restores).

    // 1. Reset expand state to the server's intent.
    var blocks = document.querySelectorAll(BRAND_SELECTOR);
    blocks.forEach(function (block) {
      var shouldExpand = block.dataset.defaultExpanded === 'true';
      block.classList.toggle('is-expanded', shouldExpand);
      var header = block.querySelector(HEADER_SELECTOR);
      if (header) header.setAttribute('aria-expanded', shouldExpand ? 'true' : 'false');
    });

    // 2. Ensure the drawer is in MODAL state. Horizon's
    // #handleCartAdd schedules a rAF that calls dialog.showModal(),
    // but in our configuration (morph-swapped drawer innards) that
    // rAF path is unreliable — by the time it fires, the captured
    // <dialog> reference may be detached, making showModal a silent
    // no-op. We reach into the CURRENT dialog ref and open it
    // ourselves with the same body-scroll-lock Horizon uses.
    var component = document.querySelector('cart-drawer-component');
    if (!component || !component.hasAttribute('auto-open')) return;
    var dialog = component.querySelector('dialog.cart-drawer__dialog');
    if (!dialog || dialog.open) return;
    try {
      dialog.showModal();
      var scrollY = window.scrollY;
      dialog.dataset.ledgePrevScrollY = String(scrollY);
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + scrollY + 'px';
    } catch (e) {
      /* no-op: dialog opened between our check and call */
    }
  }

  /**
   * When Horizon closes the dialog (via X click, outside click, Esc, or
   * its own closeDialog path), unlock body scroll. If our onCartUpdate
   * opened the dialog we installed position:fixed on body; we must
   * reverse it on close. Horizon's closeDialog does the same but only
   * when IT opened the dialog via showDialog. Listen for the native
   * "close" event on any dialog we opened.
   */
  function onDialogClose(event) {
    var dialog = event.target;
    if (!dialog || !dialog.matches || !dialog.matches('dialog.cart-drawer__dialog')) return;
    var prev = dialog.dataset.ledgePrevScrollY;
    if (prev == null) return;
    delete dialog.dataset.ledgePrevScrollY;
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: parseInt(prev, 10) || 0, behavior: 'instant' });
  }

  function init() {
    // Guard against double-init (script may be loaded twice during theme
    // editor preview refreshes). The event listeners below are idempotent
    // because browsers dedupe identical function references, but we also
    // mark the body so future loads can detect prior init.
    if (document.body.dataset.ledgeCartDrawerInit === '1') return;
    document.body.dataset.ledgeCartDrawerInit = '1';

    document.addEventListener('click', onDocumentClick);
    // Horizon's cart events bubble from the component; listen on document.
    document.addEventListener('cart:update', onCartUpdate);
    // Native <dialog> close event (fires for all close paths: X, outside
    // click, Escape, closeDialog). Delegate on document via capture
    // since `close` doesn't bubble by default.
    document.addEventListener('close', onDialogClose, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
